/**
 * Shared email validation for login and signup flows.
 * Use this utility whenever an email must be validated (e.g. on form submit).
 */

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true if the trimmed string is a valid email format.
 */
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && emailRegex.test(trimmed);
}

/**
 * Defense-in-depth twin of btcore.utils.is_beatly_email: true when this address
 * belongs to Beatly staff and must not appear in client reviewer UIs even if the
 * API returns them.
 */
export function isBeatlyEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const trimmed = email.toLowerCase().trim();
  if (trimmed.endsWith('beatly.com')) return true;
  // Keep in sync with btcore.utils.is_beatly_email known personal staff addresses.
  const knownBeatlyEmails = [
    'quy.vo@wearefram.com',
    'vongocquy@outlook.com',
    'egzona.majkovci@hotmail.se',
    'egzon.gollopeni@gmail.com',
    'siddhm98@yahoo.in',
    'viktor_stromberg@hotmail.com',
    'mikaela@trendify.se',
  ];
  return knownBeatlyEmails.includes(trimmed);
}

/**
 * Undo common query-string corruption: URLSearchParams decodes "+" as space in the
 * application/x-www-form-urlencoded grammar. For values read from ?email=...,
 * map spaces in the local part back to "+" so aliases like user+tag@x.com survive.
 */
export function normalizeEmailFromUrlQuery(decoded: string): string {
  const trimmed = decoded.trim();
  if (!trimmed) {
    return trimmed;
  }
  const at = trimmed.indexOf('@');
  if (at <= 0) {
    return trimmed;
  }
  const local = trimmed.slice(0, at).replace(/\s+/g, '+');
  return `${local}@${trimmed.slice(at + 1)}`;
}
